import React, { useState, useEffect } from 'react';
import {
  Activity,
  AlertTriangle,
  TrendingUp,
  Clock,
  Shield,
  DollarSign,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import api from '../../lib/api';

const SecurityMonitoringTab = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningChecks, setRunningChecks] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    fetchDashboard();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(fetchDashboard, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/security/monitoring/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const runManualChecks = async () => {
    try {
      setRunningChecks(true);
      await api.post('/security/monitoring/run-checks');
      await fetchDashboard();
      alert('✅ Vérifications de sécurité terminées');
    } catch (error) {
      console.error('Erreur vérifications:', error);
      alert('❌ Erreur lors des vérifications');
    } finally {
      setRunningChecks(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'HIGH':
      case 'CRITICAL':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'MEDIUM':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'WARNING':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default:
        return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'HIGH':
      case 'CRITICAL':
        return <XCircle className="w-5 h-5" />;
      case 'MEDIUM':
      case 'WARNING':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <CheckCircle2 className="w-5 h-5" />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Shield className="w-8 h-8 text-blue-600" />
            Monitoring & Alertes de Sécurité
          </h2>
          <p className="text-gray-600 mt-1">
            Surveillance en temps réel des métriques système et des menaces
          </p>
        </div>

        <button
          onClick={runManualChecks}
          disabled={runningChecks}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          {runningChecks ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Vérification...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4" />
              Lancer les vérifications
            </>
          )}
        </button>
      </div>

      {/* Statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Alertes totales */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="w-8 h-8 text-orange-600" />
            <span className="text-sm text-gray-500">Dernières 24h</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">
            {dashboard?.total_alerts || 0}
          </p>
          <p className="text-sm text-gray-600 mt-1">Alertes totales</p>
        </div>

        {/* Alertes critiques */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <XCircle className="w-8 h-8 text-red-600" />
            <span className="text-sm text-gray-500">Critiques</span>
          </div>
          <p className="text-3xl font-bold text-red-600">
            {dashboard?.alerts_by_severity?.HIGH || 0}
          </p>
          <p className="text-sm text-gray-600 mt-1">Alertes haute priorité</p>
        </div>

        {/* Performance API */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-8 h-8 text-green-600" />
            <span className="text-sm text-gray-500">Moyenne</span>
          </div>
          <p className="text-3xl font-bold text-green-600">
            {dashboard?.performance?.avg_response_time_ms?.toFixed(0) || 0}
            <span className="text-lg">ms</span>
          </p>
          <p className="text-sm text-gray-600 mt-1">Temps de réponse API</p>
        </div>

        {/* Alertes business */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-8 h-8 text-purple-600" />
            <span className="text-sm text-gray-500">Business</span>
          </div>
          <p className="text-3xl font-bold text-purple-600">
            {dashboard?.alerts_by_type?.business || 0}
          </p>
          <p className="text-sm text-gray-600 mt-1">Alertes financières</p>
        </div>
      </div>

      {/* Alertes par type */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {['security', 'business', 'performance'].map((type) => (
          <div key={type} className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-2 capitalize">{type}</h3>
            <div className="flex items-center justify-between">
              <p className="text-2xl font-bold text-gray-800">
                {dashboard?.alerts_by_type?.[type] || 0}
              </p>
              <div className={`px-3 py-1 rounded-full text-sm ${
                (dashboard?.alerts_by_type?.[type] || 0) > 0 
                  ? 'bg-red-100 text-red-700'
                  : 'bg-green-100 text-green-700'
              }`}>
                {(dashboard?.alerts_by_type?.[type] || 0) > 0 ? 'Actif' : 'OK'}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Alertes récentes */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div 
          className="p-4 border-b border-gray-200 flex items-center justify-between cursor-pointer hover:bg-gray-50"
          onClick={() => toggleSection('alerts')}
        >
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-600" />
            Alertes récentes ({dashboard?.recent_alerts?.length || 0})
          </h3>
          {expandedSection === 'alerts' ? (
            <ChevronUp className="w-5 h-5 text-gray-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-600" />
          )}
        </div>

        {expandedSection === 'alerts' && (
          <div className="p-4">
            {dashboard?.recent_alerts && dashboard.recent_alerts.length > 0 ? (
              <div className="space-y-3">
                {dashboard.recent_alerts.slice(0, 5).map((alert, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${getSeverityColor(alert.severity)}`}
                  >
                    <div className="flex items-start gap-3">
                      {getSeverityIcon(alert.severity)}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-semibold">{alert.title}</h4>
                          <span className={`px-2 py-1 text-xs font-medium rounded ${getSeverityColor(alert.severity)}`}>
                            {alert.severity}
                          </span>
                        </div>
                        <p className="text-sm mb-2">{alert.message}</p>
                        <p className="text-xs opacity-75">
                          {new Date(alert.timestamp).toLocaleString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>Aucune alerte récente</p>
                <p className="text-sm mt-1">Tous les systèmes fonctionnent normalement</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Informations */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">ℹ️ À propos du monitoring automatique</h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Les vérifications de sécurité s'exécutent automatiquement <strong>toutes les heures</strong></li>
          <li>Les alertes critiques sont envoyées par <strong>email aux Super Admins</strong></li>
          <li>Le dashboard se rafraîchit automatiquement toutes les <strong>30 secondes</strong></li>
          <li>Vous pouvez lancer des vérifications manuelles à tout moment</li>
        </ul>
      </div>
    </div>
  );
};

export default SecurityMonitoringTab;
